import React from "react";
import Section from "../ui/Section/Section";
import { Grid, Stack } from "../ui/Grid/Grid";
import { CardBody, CenterRow } from "../ui/Card/Card";
import SectionSkeleton from "../ui/SectionSkeleton/SectionSkeleton";
import KpiCard from "../ui/Card/KpiCard";
import ChartCard from "../ui/Card/ChartCard";
import Donut from "../charts/Donut";
import LegendTable from "../ui/LegendTable/LegendTable";
import HBarList from "../ui/HBarList/HBarList";
import SplitBar from "../ui/SplitBar/SplitBar";
import DeltaValue from "../ui/DeltaValue/DeltaValue";
import EmptyState from "../ui/EmptyState/EmptyState";
import { INDICATORS, KPI, SECTIONS } from "../../constants";
import { fmt1, fmt1Signed, fmtN } from "../../functions/format";
import { pctChange } from "../../functions/mapEffectif";
import { usePilotageRhStore } from "../../store/usePilotageRhStore";
import useIndicatorsVisibility from "../../hooks/useIndicatorsVisibility";

const { id, title } = SECTIONS.effectif;

/** Textes des tooltips "vs précédent" ; ajuster les suffixes ici. */
const formatDeltaTip = (delta) =>
    delta !== null ? `${fmt1Signed(delta)}% vs précédent` : undefined;

const formatPointDeltaTip = (current, previous) =>
    current !== null &&
    current !== undefined &&
    previous !== null &&
    previous !== undefined
        ? `${fmt1Signed(current - previous)} pt vs précédent`
        : undefined;

const SKELETON_ROWS = [
    { cols: 4, cards: [1, 1, 1, 1] },
    { cols: 3, cards: [5, 5, 3] },
];

const EffectifSection = ({ data, isLoading }) => {
    const resetFilters = usePilotageRhStore((s) => s.resetFilters);
    const { isVisible } = useIndicatorsVisibility();

    if (isLoading && !data) {
        return (
            <Section id={id} title={title}>
                <SectionSkeleton rows={SKELETON_ROWS} />
            </Section>
        );
    }

    if (!data || data.headcount === 0) {
        return (
            <Section id={id} title={title}>
                <EmptyState
                    title="Aucun collaborateur ne correspond à ce périmètre"
                    description="Modifiez les filtres pour afficher l'effectif."
                    actionLabel="Réinitialiser les filtres"
                    onAction={resetFilters}
                />
            </Section>
        );
    }

    const { headcount: hc } = data;

    return (
        <Section id={id} title={title}>
            <Grid cols={4} $mb>
                {isVisible(INDICATORS.headcount) && (
                    <KpiCard
                        indicator={INDICATORS.headcount}
                        label={KPI.headcount.label}
                        unit={KPI.headcount.unit}
                        value={fmtN(hc)}
                        section={id}
                        tip={formatDeltaTip(
                            pctChange(hc, data.headcountPrevious)
                        )}
                    />
                )}
                {isVisible(INDICATORS.etp) && (
                    <KpiCard
                        indicator={INDICATORS.etp}
                        label={KPI.etp.label}
                        unit={KPI.etp.unit}
                        value={fmt1(data.etp)}
                        section={id}
                        tip={formatDeltaTip(
                            pctChange(data.etp, data.etpPrevious)
                        )}
                    />
                )}
                {isVisible(INDICATORS.effectif_moyen) && (
                    <KpiCard
                        indicator={INDICATORS.effectif_moyen}
                        label={KPI.effectif_moyen.label}
                        unit={KPI.effectif_moyen.unit}
                        value={fmtN(data.effectifMoyen)}
                        section={id}
                        tip={formatDeltaTip(
                            pctChange(
                                data.effectifMoyen,
                                data.effectifMoyenPrevious
                            )
                        )}
                    />
                )}
                {isVisible(INDICATORS.evolution_effectif) && (
                    <KpiCard
                        indicator={INDICATORS.evolution_effectif}
                        label={KPI.evolution_effectif.label}
                        unit={KPI.evolution_effectif.unit}
                        value={`${data.evolution >= 0 ? "+" : ""}${fmt1(
                            data.evolution
                        )}`}
                        section={id}
                        tip={formatPointDeltaTip(
                            data.evolution,
                            data.evolutionPrevious
                        )}
                    />
                )}
            </Grid>

            <Grid cols={3}>
                {isVisible(INDICATORS.repartition_contrat) && (
                    <ChartCard
                        indicator={INDICATORS.repartition_contrat}
                        title="Répartition par contrat"
                        fill
                    >
                        <CardBody $gap="10px">
                            <CenterRow>
                                <Donut
                                    segments={data.contratRows.map((r) => ({
                                        value: r.value,
                                        color: r.color,
                                        tip: `${r.name} : ${fmtN(
                                            r.value
                                        )} collab.`,
                                    }))}
                                    center={fmtN(hc)}
                                    sub="ACTIFS"
                                />
                            </CenterRow>
                            <LegendTable rows={data.contratRows} total={hc} />
                        </CardBody>
                    </ChartCard>
                )}

                {isVisible(INDICATORS.repartition_departement) && (
                    <ChartCard
                        indicator={INDICATORS.repartition_departement}
                        title="Répartition par département"
                    >
                        <HBarList items={data.deptRows} />
                    </ChartCard>
                )}

                {isVisible(
                    INDICATORS.categorie_professionnelle,
                    INDICATORS.taux_activite
                ) && (
                    <Stack>
                        {isVisible(INDICATORS.categorie_professionnelle) && (
                            <ChartCard
                                indicator={INDICATORS.categorie_professionnelle}
                                title="Catégorie professionnelle"
                            >
                                <SplitBar
                                    left={{
                                        label: "Cadres",
                                        value: (
                                            <DeltaValue
                                                section={id}
                                                tip={formatPointDeltaTip(
                                                    data.cadresPct,
                                                    data.cadresPctPrevious
                                                )}
                                            >
                                                {data.cadresPct}%
                                            </DeltaValue>
                                        ),
                                        pct: data.cadresPct,
                                        tip: `Cadres : ${fmtN(
                                            data.cadresCount
                                        )} collab.`,
                                    }}
                                    right={{
                                        label: "Non-cadres",
                                        value: `${100 - data.cadresPct}%`,
                                        tip: `Non-cadres : ${fmtN(
                                            hc - data.cadresCount
                                        )} collab.`,
                                    }}
                                />
                            </ChartCard>
                        )}
                        {isVisible(INDICATORS.taux_activite) && (
                            <ChartCard
                                indicator={INDICATORS.taux_activite}
                                title="Taux d’activité"
                            >
                                <SplitBar
                                    left={{
                                        label: "Temps plein",
                                        value: (
                                            <DeltaValue
                                                section={id}
                                                tip={formatPointDeltaTip(
                                                    data.tempsPleinPct,
                                                    data.tempsPleinPctPrevious
                                                )}
                                            >
                                                {data.tempsPleinPct}%
                                            </DeltaValue>
                                        ),
                                        pct: data.tempsPleinPct,
                                        tip: `Temps plein : ${fmtN(
                                            data.tempsPleinCount
                                        )} collab.`,
                                    }}
                                    right={{
                                        label: "Temps partiel",
                                        value: `${fmt1(
                                            100 - data.tempsPleinPct
                                        )}%`,
                                        tip: `Temps partiel : ${fmtN(
                                            hc - data.tempsPleinCount
                                        )} collab.`,
                                    }}
                                />
                            </ChartCard>
                        )}
                    </Stack>
                )}
            </Grid>
        </Section>
    );
};

export default EffectifSection;
